import {
  AshbyDepartment,
  AshbyLocation,
  FilterOption,
  GetJobsBoardViewDataParams,
  Job,
  JobsBoardViewData,
  JobsByDepartmentName,
} from './types';

export const splitCamelCase = (word: string): string =>
  word.replace(/([a-z])([A-Z])/g, (_, lower, upper) => `${lower} ${upper.toLowerCase()}`);

export const getLocationsFromAshby = (locations: AshbyLocation[]): FilterOption[] => {
  const byExternalName = new Map<string, FilterOption>();

  for (const { externalName, id } of locations) {
    if (externalName && !byExternalName.has(externalName)) {
      byExternalName.set(externalName, { id, label: externalName, value: externalName });
    }
  }

  return Array.from(byExternalName.values()).sort((a, b) => {
    const aParts = a.label.split(',');
    const bParts = b.label.split(',');

    return (
      (aParts.at(-1) ?? '').localeCompare(bParts.at(-1) ?? '') || (aParts.at(0) ?? '').localeCompare(bParts.at(0) ?? '')
    );
  });
};

export const getDepartmentsFromAshby = (departments: AshbyDepartment[]): FilterOption[] =>
  departments
    .filter((department) => !department.isArchived && !department.parentId)
    .map((department) => ({
      id: department.id,
      label: department.externalName || department.name,
      value: department.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const groupJobsByDepartmentName = (jobs: Job[], departments: FilterOption[]): JobsByDepartmentName[] => {
  const groupedJobs = jobs.reduce<Map<string, JobsByDepartmentName>>((acc, job) => {
    const departmentName = job.departmentName;

    if (!departmentName) {
      return acc;
    }

    const existingGroup = acc.get(departmentName);

    if (existingGroup) {
      existingGroup.jobs.push(job);
      return acc;
    }

    acc.set(departmentName, {
      id: departmentName,
      name: departmentName,
      externalLabel: departments?.find((dept) => dept.value === departmentName)?.label ?? departmentName,
      jobs: [job],
    });

    return acc;
  }, new Map());

  return Array.from(groupedJobs.values()).sort((a, b) => a.externalLabel.localeCompare(b.externalLabel));
};

export const getSelectedFilterValues = (value?: string | string[]) => {
  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [];
};

export const getJobsBoardViewData = ({
  jobsByDepartmentName,
  filter,
  page,
  itemsPerPage,
  departments,
}: GetJobsBoardViewDataParams): JobsBoardViewData => {
  const departmentFilters = getSelectedFilterValues(filter.department);
  const locationFilters = getSelectedFilterValues(filter.location);

  const filteredDepartmentGroups = jobsByDepartmentName
    .filter((group) => !departmentFilters.length || departmentFilters.includes(group.name))
    .map((group) => {
      const filteredJobs = group.jobs.filter(
        (job) => !locationFilters.length || locationFilters.includes(job.locationExternalName ?? ''),
      );
      if (!filteredJobs.length) return undefined;
      return {
        ...group,
        jobs: filteredJobs.sort((a, b) => a.title.localeCompare(b.title)),
      };
    })
    .filter((group): group is JobsByDepartmentName => !!group)
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredJobs = filteredDepartmentGroups.flatMap((group) => group.jobs);
  const paginatedJobs = filteredJobs.slice(itemsPerPage * (page - 1), itemsPerPage * page);

  return {
    items: groupJobsByDepartmentName(paginatedJobs, departments),
    total: Math.ceil(filteredJobs.length / itemsPerPage),
  };
};
